import React, { useState } from 'react';

import FilterSelectButton from './FilterSelectButton';
import useOutsideClick from '../../../hooks/useOutsideClick';
import FilterMenu from './FilterMenu';

import styles from './FilterSelect.scss';

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

export interface FilterSelectProps {
  title: string;
  label?: string;
  ariaLabel?: string;
  isOpen?: boolean;
  isApplyDisabled?: boolean;
  className?: string;
  options: FilterOption[];
  selectedValues: string[];
  isSelectButtonDisabled?: boolean;
  isSearchable?: boolean;
  onChange: (values: string[]) => void;
  onApply?: () => void;
  onClose?: () => void;
}

function FilterSelect(props: FilterSelectProps): React.ReactElement {
  const {
    title,
    label,
    ariaLabel,
    onChange,
    onApply = () => {},
    onClose = () => {},
    options,
    selectedValues,
    isApplyDisabled,
    className,
    isSelectButtonDisabled = false,
    isSearchable,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const onCloseHandler = () => {
    onClose();
    setIsOpen(false);
  };

  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen((prevValue) => !prevValue);
    e.stopPropagation();
  };

  const onApplyHandler = () => {
    onApply();
    setIsOpen(false);
  };

  const onChangeHandler = (_: unknown, value: string) => {
    if (selectedValues.indexOf(value) >= 0) {
      const updatedValues = selectedValues.filter((prevValue) => prevValue !== value);
      onChange(updatedValues);
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const ref = useOutsideClick(onCloseHandler);

  return (
    <div ref={isOpen ? ref : null} className={styles.container}>
      <FilterSelectButton
        disabled={isSelectButtonDisabled}
        isActive={isOpen}
        label={label}
        ariaLabel={ariaLabel}
        onClick={onButtonClick}
      >
        {title}
      </FilterSelectButton>
      {isOpen && (
        <FilterMenu
          options={options}
          selectedValues={selectedValues}
          className={className}
          isApplyDisabled={isApplyDisabled}
          isSearchable={isSearchable}
          onChange={onChangeHandler}
          onApply={onApplyHandler}
        />
      )}
    </div>
  );
}

export default React.memo(FilterSelect);
